import { Link } from "react-router-dom";
import { ShoppingCart, Star } from "lucide-react";
import { inr } from "@/lib/api";

// Request a lighter rendition from the image CDN (source URLs are 3840px) for faster loads
const lightImg = (url) => (url || "").replace("_SL3840_QL80", "_SL800_QL70");

export function RxBadge({ rx }) {
  return rx ? (
    <span data-testid="product-card-rx-badge" className="bg-amber-100 text-amber-800 text-[10px] font-bold px-2 py-0.5 rounded border border-amber-300 uppercase tracking-wide">Rx</span>
  ) : (
    <span data-testid="product-card-otc-badge" className="bg-emerald-50 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded border border-emerald-200 uppercase tracking-wide">OTC</span>
  );
}

export default function ProductCard({ product, index = 0 }) {
  const discount = product.mrp && product.mrp > product.price ? Math.round((1 - product.price / product.mrp) * 100) : 0;
  const rating = product.rating ?? 4.7;
  const reviews = product.reviews ?? 120;

  return (
    <article
      className="group bg-white border border-slate-200 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 p-4 relative flex flex-col"
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/product/${product.id}`} className="relative block rounded-xl overflow-hidden bg-slate-50 aspect-square" data-testid={`product-link-${product.id}`}>
        <img
          src={lightImg(product.image)}
          alt={product.name}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
        />
        {discount > 0 && (
          <span data-testid={`product-discount-badge-${product.id}`} className="absolute top-3 left-3 bg-rose-500 text-white font-extrabold text-xs px-2.5 py-1 rounded-full shadow-sm">
            {discount}% Off
          </span>
        )}
        <div className="absolute top-3 right-3"><RxBadge rx={product.rx} /></div>
      </Link>

      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mt-3">{product.category}</p>
      <Link to={`/product/${product.id}`} className="font-semibold text-sm text-slate-900 leading-snug mt-1 hover:text-emerald-800 transition-colors line-clamp-2">
        {product.name}
      </Link>

      {product.tagline && (
        <p className="text-xs font-medium text-emerald-800 bg-emerald-50 px-2 py-1 rounded-md my-2 line-clamp-1 border border-emerald-100/60 w-fit" data-testid={`product-tagline-${product.id}`}>
          {product.tagline}
        </p>
      )}

      <div className="flex items-center gap-1.5 text-xs text-slate-600 mt-1" data-testid={`product-rating-${product.id}`}>
        <div className="flex gap-0.5">
          {[0, 1, 2, 3, 4].map((s) => (
            <Star key={s} size={12} className={s < Math.round(rating) ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
          ))}
        </div>
        <span className="font-semibold">{rating}</span>
        <span className="text-slate-400">| {reviews.toLocaleString("en-IN")} reviews</span>
      </div>

      <div className="grid grid-cols-2 gap-2 my-3 mt-auto">
        <div className="border border-slate-200 rounded-lg bg-slate-50 px-3 py-1.5">
          <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">MRP <span className="normal-case font-medium">/ strip</span></p>
          <p className="font-mono-ed text-sm font-semibold text-slate-600" data-testid={`product-mrp-${product.id}`}>{inr(product.mrp)}</p>
        </div>
        <div className="border border-emerald-200 rounded-lg bg-emerald-50 px-3 py-1.5">
          <p className="text-[9px] font-bold uppercase tracking-wider text-emerald-600">PTR <span className="normal-case font-medium">/ strip</span></p>
          <p className="font-mono-ed text-base font-bold text-emerald-900" data-testid={`product-price-${product.id}`}>{inr(product.price)}</p>
        </div>
      </div>

      <Link
        to={`/product/${product.id}`}
        data-testid={`product-add-to-cart-btn-${product.id}`}
        className="w-full py-2.5 px-4 bg-emerald-800 hover:bg-emerald-900 active:scale-[0.98] text-white font-bold text-sm rounded-xl shadow-md transition-all flex items-center justify-center gap-2"
      >
        <ShoppingCart size={15} /> Select Pack & Order
      </Link>
    </article>
  );
}

